import React, { useEffect, useState, type FC } from "react";

import { useProductSearchQuery } from "../../ProductSearchContextProvider";
import { useBeforeTypingProducts } from "../../hooks/useBeforeTypingProducts";
import useAlgoliaAutocomplete from "../../hooks/useAlgoliaAutocomplete";
import {
  DEFAULT_TYPING_STATE_STRATEGY,
  type TypingStateStrategy,
} from "@/monarch/rules/productSearch/types";

import { ProductSearchListItemProps } from "./ProductSearchListItem";
import { Category } from "../../hooks/useAlgoliaAutocomplete";

import DefaultState from "./DefaultState";
import AfterState from "./AfterState";
import NoResults from "./NoResultsState";
import ErrorState from "./ErrorState";
import LoadingState from "./LoadingState";
import Footer from "./Footer";
import useTranslations from "@/i18n/useTranslations";

enum State {
  BEFORE = "BEFORE", // Before user types State
  LOADING = "LOADING", // While search data is being fetched
  AFTER = "AFTER", // After user types state
  NO_RESULTS = "NO_RESULTS", // When results come back
  ERROR = "ERROR", // When an error occurs when searching
}

interface StateComponentProps {
  featuredItems: ProductSearchListItemProps[];
  categories: Category[];
  resultItems: ProductSearchListItemProps[];
  query: string;
  featuredCategories?: {
    label: string;
    url: string;
  }[];
  trendingSectionHeader?: string;
  mobileCategoryLimit?: number;
  learnerFavoritesSectionHeader?: string;
  noResultsHeader?: string;
  noResultsBullets?: string[];
  location?: string;
}

const UIStateMap: Record<State, FC<StateComponentProps>> = {
  [State.BEFORE]: (props) => (
    <DefaultState
      featuredItems={props.featuredItems}
      featuredCategories={props.featuredCategories}
      trendingSectionHeader={props.trendingSectionHeader}
      mobileCategoryLimit={props.mobileCategoryLimit}
      learnerFavoritesSectionHeader={props.learnerFavoritesSectionHeader}
      location={props.location}
    />
  ),
  [State.LOADING]: () => <LoadingState />,
  [State.AFTER]: (props) => (
    <AfterState
      categories={props.categories}
      resultItems={props.resultItems}
      query={props.query}
      location={props.location}
    />
  ),
  [State.NO_RESULTS]: (props) => (
    <NoResults
      featuredCategories={props.featuredCategories}
      featuredItems={props.featuredItems}
      trendingSectionHeader={props.trendingSectionHeader}
      mobileCategoryLimit={props.mobileCategoryLimit}
      learnerFavoritesSectionHeader={props.learnerFavoritesSectionHeader}
      noResultsHeader={props.noResultsHeader}
      noResultsBullets={props.noResultsBullets}
      location={props.location}
    />
  ),
  [State.ERROR]: (props) => <ErrorState location={props.location} />,
};

interface Props {
  featuredPrograms?: string[];
  featuredCategories?: {
    label: string;
    url: string;
  }[];
  trendingSectionHeader?: string;
  mobileCategoryLimit?: number;
  learnerFavoritesSectionHeader?: string;
  noResultsHeader?: string;
  noResultsBullets?: string[];
  footerText?: string;
  footerUrl?: string;
  footerLinkText?: string;
  location?: string;
  typingStateStrategy?: TypingStateStrategy;
  querySuggestionsIndex?: string;
}

const ProductSuggestions: FC<Props> = (props) => {
  const t = useTranslations("Search");
  // state management
  const [uiState, setUiState] = useState(State.BEFORE);

  const {
    featuredPrograms: featuredProgramsUUIDs,
    featuredCategories = [
      { label: "Business", url: "/search?subject=Business" },
      { label: "Computer Science", url: "/search?subject=Computer%20Science" },
      { label: "Data Science", url: "/search?subject=Data%20Science" },
    ],
    trendingSectionHeader,
    mobileCategoryLimit,
    learnerFavoritesSectionHeader,
    footerText: footerTextProp,
    footerUrl,
    footerLinkText,
    typingStateStrategy = DEFAULT_TYPING_STATE_STRATEGY,
    querySuggestionsIndex,
  } = props;

  const {
    featuredPrograms,
    isLoading: beforeTypingIsLoading,
    error,
  } = useBeforeTypingProducts(props.featuredPrograms ?? []);

  // data fetch
  const { query, resetSearch } = useProductSearchQuery();
  const results = useAlgoliaAutocomplete(
    query,
    typingStateStrategy,
    querySuggestionsIndex,
  );

  useEffect(() => {
    if (results.hasError) {
      setUiState(State.ERROR);
      return;
    }

    if (!query || query.trim().length === 0) {
      setUiState(State.BEFORE);
      return;
    }

    if (results.isLoading) {
      setUiState(State.LOADING);
      return;
    }

    if (results.products.length > 0 || results.categories.length > 0) {
      setUiState(State.AFTER);
      return;
    }

    setUiState(State.NO_RESULTS);
  }, [query, results]);

  // current state
  const Component: FC<StateComponentProps> = UIStateMap[uiState];

  // Pass what the screens need
  // Build stateProps with all data needed by child components
  const stateProps: StateComponentProps = {
    featuredItems: featuredPrograms,
    categories: results.categories,
    resultItems: results.products,
    query: query || "",
    featuredCategories,
    trendingSectionHeader,
    mobileCategoryLimit,
    learnerFavoritesSectionHeader,
    noResultsHeader: props.noResultsHeader,
    noResultsBullets: props.noResultsBullets,
    location: props.location,
  };

  const footerText =
    footerTextProp ??
    (uiState === State.AFTER
      ? t("pressEnterToSeeAllResults", { query: query ?? "" })
      : t("idleFooterFallback"));

  return (
    <div className="w-full max-md:h-full max-md:min-h-0">
      <div className="w-full overflow-hidden rounded-t-none rounded-b-[6px] border-x border-b border-putty-500 border-t-0 bg-white max-md:flex max-md:h-full max-md:min-h-0 max-md:flex-col max-md:rounded-none max-md:border-x-0">
        <div
          aria-hidden
          className="h-px w-full shrink-0 bg-black/14 max-md:hidden"
        />
        <div className="p-6 max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:overscroll-y-contain">
          <Component {...stateProps} />
        </div>
        <div className="shrink-0">
          <Footer
            text={footerText}
            url={footerUrl}
            linkText={footerLinkText}
            isError={uiState === State.ERROR}
            onRetry={() => resetSearch()}
            location={props.location}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductSuggestions;
